import { useCallback, useEffect, useRef, useState } from 'react'
import { allowsOptionalStorage, CONSENT_CHANGE_EVENT, type ConsentChoice } from '../config/consent'
import { siteConfig } from '../config/site'

export type ThemeMode = 'auto' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

type DateParts = { year: number; month: number; day: number }

const radians = (degrees: number) => degrees * Math.PI / 180
const degrees = (value: number) => value * 180 / Math.PI
const normalize = (value: number, maximum: number) => ((value % maximum) + maximum) % maximum

function athensDateParts(date: Date): DateParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: siteConfig.theme.timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
  const parts = formatter.formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value)
  return { year: get('year'), month: get('month'), day: get('day') }
}

function calculateSolarEvent(parts: DateParts, sunrise: boolean): Date | null {
  const { latitude, longitude } = siteConfig.theme
  const start = Date.UTC(parts.year, 0, 0)
  const current = Date.UTC(parts.year, parts.month - 1, parts.day)
  const dayOfYear = Math.floor((current - start) / 86_400_000)
  const longitudeHour = longitude / 15
  const approximateTime = dayOfYear + ((sunrise ? 6 : 18) - longitudeHour) / 24
  const meanAnomaly = 0.9856 * approximateTime - 3.289
  let trueLongitude = meanAnomaly
    + 1.916 * Math.sin(radians(meanAnomaly))
    + 0.02 * Math.sin(radians(2 * meanAnomaly))
    + 282.634
  trueLongitude = normalize(trueLongitude, 360)

  let rightAscension = degrees(Math.atan(0.91764 * Math.tan(radians(trueLongitude))))
  rightAscension = normalize(rightAscension, 360)
  const longitudeQuadrant = Math.floor(trueLongitude / 90) * 90
  const ascensionQuadrant = Math.floor(rightAscension / 90) * 90
  rightAscension = (rightAscension + longitudeQuadrant - ascensionQuadrant) / 15

  const sinDeclination = 0.39782 * Math.sin(radians(trueLongitude))
  const cosDeclination = Math.cos(Math.asin(sinDeclination))
  const cosHourAngle = (
    Math.cos(radians(90.833)) - sinDeclination * Math.sin(radians(latitude))
  ) / (cosDeclination * Math.cos(radians(latitude)))

  if (cosHourAngle > 1 || cosHourAngle < -1) return null

  let hourAngle = sunrise
    ? 360 - degrees(Math.acos(cosHourAngle))
    : degrees(Math.acos(cosHourAngle))
  hourAngle /= 15

  const localMeanTime = hourAngle + rightAscension - 0.06571 * approximateTime - 6.622
  const utcHours = normalize(localMeanTime - longitudeHour, 24)

  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day) + utcHours * 3_600_000)
}

function resolveAutomaticTheme(now: Date) {
  const today = athensDateParts(now)
  const sunrise = calculateSolarEvent(today, true)
  const sunset = calculateSolarEvent(today, false)

  if (!sunrise || !sunset) {
    const fallback = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    return { theme: fallback as ResolvedTheme, nextTransition: null }
  }

  if (now < sunrise) return { theme: 'dark' as const, nextTransition: sunrise }
  if (now < sunset) return { theme: 'light' as const, nextTransition: sunset }

  const tomorrow = new Date(now.getTime() + 86_400_000)
  const nextSunrise = calculateSolarEvent(athensDateParts(tomorrow), true)
  return { theme: 'dark' as const, nextTransition: nextSunrise }
}

function readInitialMode(): ThemeMode {
  if (typeof document === 'undefined') return siteConfig.theme.defaultMode
  const stored = document.documentElement.dataset.themeMode
  return stored === 'light' || stored === 'dark' || stored === 'auto' ? stored : siteConfig.theme.defaultMode
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(
    typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light' ? 'light' : 'dark',
  )
  const timer = useRef<number | null>(null)

  const applyMode = useCallback(function updateTheme(selectedMode: ThemeMode) {
    const result = selectedMode === 'auto'
      ? resolveAutomaticTheme(new Date())
      : { theme: selectedMode, nextTransition: null }

    document.documentElement.dataset.theme = result.theme
    document.documentElement.dataset.themeMode = selectedMode
    document.documentElement.style.colorScheme = result.theme
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', result.theme === 'light' ? '#f3efe5' : '#090909')
    setResolvedTheme(result.theme)

    if (timer.current) window.clearTimeout(timer.current)
    if (selectedMode === 'auto' && result.nextTransition) {
      const delay = Math.max(1_000, result.nextTransition.getTime() - Date.now() + 1_000)
      timer.current = window.setTimeout(() => updateTheme('auto'), Math.min(delay, 2_147_483_647))
    }
  }, [])

  const setMode = useCallback((nextMode: ThemeMode) => {
    try {
      if (allowsOptionalStorage()) {
        localStorage.setItem(siteConfig.theme.storageKey, nextMode)
      } else {
        localStorage.removeItem(siteConfig.theme.storageKey)
      }
    } catch {
      // Applying a theme does not depend on storage being available.
    }
    setModeState(nextMode)
    applyMode(nextMode)
  }, [applyMode])

  useEffect(() => {
    applyMode(mode)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && mode === 'auto') applyMode('auto')
    }
    const handlePreference = () => {
      if (mode === 'auto') applyMode('auto')
    }
    const handleConsentChange = (event: Event) => {
      const choice = (event as CustomEvent<ConsentChoice>).detail

      try {
        if (choice === 'accepted') {
          const activeMode = document.documentElement.dataset.themeMode
          if (activeMode === 'auto' || activeMode === 'light' || activeMode === 'dark') {
            localStorage.setItem(siteConfig.theme.storageKey, activeMode)
          }
        } else {
          localStorage.removeItem(siteConfig.theme.storageKey)
        }
      } catch {
        // Consent remains valid even when optional storage is unavailable.
      }
    }
    const preference = window.matchMedia('(prefers-color-scheme: dark)')
    document.addEventListener('visibilitychange', handleVisibility)
    preference.addEventListener('change', handlePreference)
    window.addEventListener(CONSENT_CHANGE_EVENT, handleConsentChange)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
      document.removeEventListener('visibilitychange', handleVisibility)
      preference.removeEventListener('change', handlePreference)
      window.removeEventListener(CONSENT_CHANGE_EVENT, handleConsentChange)
    }
  }, [applyMode, mode])

  return { mode, resolvedTheme, setMode }
}
